import type { LandingConfig } from "../components/SeoLandingPage";

/* C4 dosha glossary (2026-11-03): pitra, guru chandal, grahan, nadi,
   kemdrum. Manglik and kaal sarp already have their own pages
   (/manglik-calculator, /kaal-sarp-dosha, /blog/manglik-dosha-explained),
   so these pages LINK to them and never re-cover them.

   Framing rules: classical definition first, then the cancellation
   conditions, then the honest frequency (computed from sign arithmetic, e.g.
   one graha sharing a sign with Rahu is 1 in 12), so a reader sees how
   common a "dosha" is before being frightened by it. No paid remedy is
   pushed, nothing is guaranteed, and every page carries a YMYL disclaimer.
   Where the term itself is modern or the texts disagree, the page says so. */

const doshaChips = [
  { to: "/free-kundli", label: "Free kundli" },
  { to: "/talk-to-astrologer", label: "Jyotishi se poochhiye" },
];

const otherDoshaChips = [
  { to: "/manglik-calculator", label: "Manglik dosha" },
  { to: "/kaal-sarp-dosha", label: "Kaal sarp dosha" },
  { to: "/sade-sati-calculator", label: "Sade sati" },
];

const doshaDisclaimer =
  "Yeh page classical jyotish ki shaikshik vyakhya hai. Koi bhi dosha akele kisi ghatna ka faisla nahi karta, aur yahan kisi upay ka koi parinaam guarantee nahi kiya gaya. Swasthya, vivah, paisa ya kanooni faislon ke liye yogya doctor, counsellor ya salahkar se salah lijiye. Dosha ke naam par dar dikhakar mehenge upay bechne waalon se saavdhaan rahiye.";

/* ------------------------------------------------------------- PITRA DOSH */

export const pitraDosh: LandingConfig = {
  url: "https://astroaura.ai/pitra-dosh",
  wide: true,
  title: "Pitra Dosh: Kundli Me Kaise Banta Hai, Kab Cancel | Aura AI",
  description:
    "Pitra dosh kundli me kin yogon se banta hai, kab cancel maana jaata hai, aur kyun ise dar se nahi dekhna chahiye. Paribhasha, bhang ke niyam aur FAQ.",
  eyebrow: "Dosha Glossary",
  h1Pre: "Pitra Dosh:",
  h1Accent: "paribhasha, bhang aur sach",
  sub: "Surya, navam bhav aur Rahu-Ketu se juda yeh yog kya hai, kab kamzor ya cancel maana jaata hai, aur parampara me iska shant upay kya bataya gaya hai.",
  primaryCta: "Apni kundli free check kijiye",
  secondaryCta: "Jyotishi se poochhiye",
  lede: [
    "Pitra dosh ka seedha arth hai poorvajon se juda dosh. Kundli me ise mukhya roop se Surya (pita ka karak), navam bhav (pita, poorvaj aur bhagya ka bhav) aur Rahu-Ketu ke sambandh se dekha jaata hai. Ek imaandaar baat pehle: 'Pitra dosh' naam Brihat Parashara Hora Shastra jaise mool granthon me isi roop me nahi milta. Wahan 'pitri shaap' jaise yogon ki charcha hai, aur aaj ki paribhasha baad ki parampara ne banayi hai. Isliye alag alag jyotishi ise alag tarah define karte hain.",
  ],
  heroFacts: ["Karak: Surya, navam bhav", "Grah: Rahu, Ketu, Shani", "Bhang ke niyam hain"],
  sections: [
    {
      h2: "Kundli me pitra dosh kin yogon se banta hai",
      body: [
        "Sabse aam taur par jo sthitiyan Pitra dosh ke naam se padhi jaati hain, woh yeh hain. Inme se ek bhi ho to kai log 'Pitra dosh hai' keh dete hain, lekin classical drishti me asar ki maatra kai baaton par tikti hai.",
      ],
      bullets: [
        "Surya ke saath Rahu (ya Ketu) ek hi bhav me, khaas kar navam bhav me.",
        "Navam bhav me Rahu ya Ketu, aur navam bhav par koi shubh drishti na ho.",
        "Navam bhav ka swami (navmesh) Rahu, Ketu ya Shani ke saath peedit ho, ya dusthan (6, 8, 12) me kamzor baitha ho.",
        "Surya aur Shani ki yuti ya aamne-saamne ki sthiti, jise pita-putra ke sambandh me tanaav se joda jaata hai.",
      ],
      chips: doshaChips,
    },
    {
      h2: "Pitra dosh kab kamzor ya cancel maana jaata hai",
      body: [
        "Yahi hissa aksar chhod diya jaata hai. Ek yog ka hona aur uska asar karna do alag baatein hain. Neeche ki sthitiyon me classical jyotishi ise kamzor ya bhang maante hain:",
      ],
      bullets: [
        "Guru ki drishti navam bhav, navmesh ya Surya par ho. Guru ki shubh drishti ko sabse bada rakshak maana jaata hai.",
        "Surya apni uchch rashi (Mesh) ya swarashi (Simha) me ho, yaani itna balwan ki Rahu ka prabhav seemit rahe.",
        "Navmesh kendra ya trikon me balwan ho, apni ya uchch rashi me.",
        "Rahu aur Surya ek rashi me hon par degree me door, to yuti dheeli maani jaati hai.",
        "Lagnesh balwan ho aur kundli ka samagra bal achha ho; tab ek akela yog jeevan ki disha tay nahi karta.",
      ],
    },
    {
      h2: "Kitna aam hai yeh yog?",
      body: [
        "Sirf ganit dekhiye. Surya kisi bhi samay baarah rashiyon me se ek me hota hai, aur Rahu bhi. Isliye Surya ke saath Rahu ki rashi-yuti lagbhag har baarahve chart me, aur Rahu ya Ketu me se kisi ek ke saath lagbhag har chhathe chart me milti hai. Itna aam yog apne aap me 'shaap' nahi ho sakta. Isliye chart ko poora padhna zaroori hai: kaunsa bhav, kaunsi rashi, kitni degree ki doori, aur Guru kahan hai.",
      ],
    },
    {
      h2: "Parampara me bataye gaye shant upay",
      body: [
        "Pitra dosh ke liye parampara jo upay batati hai, woh zyaadatar muft aur vyavaharik hain, mehenge nahi. Pitru Paksha me shraddha aur tarpan, apne sthan par Amavasya ko poorvajon ka smaran, maata-pita aur bujurgon ki seva, aur apni kshamata ke anusaar daan. In sabka mool bhaav kritagyata hai, dar nahi.",
        "Agar koi aapse kahe ki bina hazaron rupaye ki puja ke 'Pitra dosh' aapke parivaar ko nuksaan pahunchaayega, to ek doosri raay zaroor lijiye. Classical jyotish dar bechne ke liye nahi bana.",
      ],
      chips: [{ to: "/panchang", label: "Panchang (Amavasya)" }],
    },
    {
      h2: "Apni kundli me kaise check karein",
      steps: [
        "Free kundli banaiye: janm ki date, samay aur jagah daaliye.",
        "Dekhiye Surya, Rahu aur Ketu kis bhav aur rashi me hain, aur navam bhav ka swami kahan hai.",
        "Guru ki drishti navam bhav ya Surya par hai ya nahi, yeh check kijiye.",
        "Koi uljhan ho to usi chart par AI jyotishi se seedha poochhiye; woh bhang ke niyam bhi ginata hai.",
      ],
      chips: otherDoshaChips,
    },
  ],
  faqs: [
    {
      q: "Pitra dosh kaise pata chalta hai?",
      a: "Kundli me Surya, navam bhav aur navmesh ki sthiti dekhkar, khaas kar unka Rahu, Ketu ya Shani se sambandh. Sirf ek yog dekhkar faisla nahi hota; Guru ki drishti aur grahon ka bal bhi dekha jaata hai.",
    },
    {
      q: "Kya Pitra dosh se shadi ya santaan me rukawat aati hai?",
      a: "Kuchh parampara aisa maanti hai, lekin yeh koi pakka niyam nahi. Ek aam yog, jo lagbhag har chhathe chart me kisi roop me milta hai, akela aise bade parinaam tay nahi karta. Swasthya ya santaan se jude sawaalon me doctor ki salah sabse pehle aati hai.",
    },
    {
      q: "Kya Pitra dosh hamesha ke liye rehta hai?",
      a: "Yog janm-kundli me rehta hai, lekin uska asar aam taur par sambandhit grahon ki dasha-antardasha me zyada mehsoos kiya jaata hai, aur bhang ki sthitiyan use kaafi had tak kamzor kar deti hain.",
    },
    {
      q: "Pitra dosh ka sabse saral upay kya hai?",
      a: "Parampara me Pitru Paksha me shraddha-tarpan, Amavasya ko poorvajon ka smaran, aur maata-pita va bujurgon ki seva. Yeh sab muft hain. Kisi mehenge upay ki koi guarantee nahi hoti.",
    },
    {
      q: "Pitra dosh aur Grahan dosh me kya farak hai?",
      a: "Dono me Surya-Rahu ki yuti aa sakti hai, isliye overlap hai. Pitra dosh ka focus navam bhav aur poorvajon par hai; Grahan dosh ka focus Surya ya Chandra ke Rahu-Ketu se 'grahan' par.",
    },
  ],
  schemaName: "Pitra Dosh",
  schemaDescription:
    "What pitra dosh means in a Vedic birth chart, the placements usually read as forming it, the conditions that weaken or cancel it, and the traditional no-cost observances.",
  finalHeading: "Dar nahi, apna chart padhiye",
  finalText:
    "Free kundli banaiye aur dekhiye ki aapke chart me yeh yog hai bhi ya nahi, aur hai to uske bhang ki sthitiyan kya hain.",
  articleSection: "Jyotish Glossary",
  disclaimer: doshaDisclaimer,
  relatedLinks: [
    { to: "/grahan-dosh", label: "grahan dosh" },
    { to: "/guru-chandal-dosh", label: "guru chandal dosh" },
  ],
};

/* ------------------------------------------------------ GURU CHANDAL DOSH */

export const guruChandalDosh: LandingConfig = {
  url: "https://astroaura.ai/guru-chandal-dosh",
  wide: true,
  title: "Guru Chandal Dosh: Yog, Cancellation Aur Sach | Aura AI",
  description:
    "Guru Chandal dosh Guru aur Rahu ki yuti se banta hai. Kab yeh kamzor ya cancel hota hai, kitna aam hai, aur bina dar ke ise kaise samjhein, sab yahan.",
  eyebrow: "Dosha Glossary",
  h1Pre: "Guru Chandal Dosh:",
  h1Accent: "Guru aur Rahu ka yog",
  sub: "Gyaan ke grah Guru par Rahu ka prabhav kya maana jaata hai, kab yeh yog kamzor padta hai, aur kab yeh ulta asaadharan safalta bhi deta hai.",
  primaryCta: "Apni kundli free check kijiye",
  secondaryCta: "Jyotishi se poochhiye",
  lede: [
    "Guru Chandal dosh (ya Guru Chandal yog) tab maana jaata hai jab kundli me Guru (Brihaspati) aur Rahu ek hi bhav me hon. Kuchh jyotishi Guru-Ketu yuti ya Guru par Rahu ki drishti ko bhi isme ginte hain. Yeh naam bhi baad ki parampara ka hai; mool granthon me isi naam se yeh dosh nahi milta. Iska saar yeh hai ki Guru, jo gyaan, guru, dharm aur vivek ka karak hai, Rahu ke bhram aur asantosh se rang jaata hai.",
  ],
  heroFacts: ["Yog: Guru + Rahu", "Har ~12 me 1 chart", "Bhang ke niyam hain"],
  sections: [
    {
      h2: "Guru Chandal dosh ki classical paribhasha",
      bullets: [
        "Mukhya roop: Guru aur Rahu ek hi rashi aur bhav me.",
        "Kuchh paramparaon me: Guru aur Ketu ki yuti bhi, ya Rahu ki Guru par drishti.",
        "Asar ki maatra: jitni kareeb degree me yuti, utna gahra maana jaata hai; ek rashi me hokar door degree ho to dheela.",
        "Bhav ka mahatva: lagna, panchwa (santaan, vidya) ya navam (dharm, guru) bhav me hone par ise zyada dhyaan se padha jaata hai.",
      ],
      body: [
        "Paramparik vyakhya me is yog ke saath vichaaron me bhram, guruon ya buzurgon se matbhed, ya niyamon se hatkar sochne ki pravritti joda jaata hai. Lekin yahi ganit doosri taraf bhi jaata hai: Rahu Guru ke gyaan ko nayi, aparamparik dishaon me le jaata hai, aur research, technology ya videsh se jude kaam me aise log aksar aage nikalte hain.",
      ],
      chips: doshaChips,
    },
    {
      h2: "Kab kamzor ya cancel maana jaata hai",
      bullets: [
        "Guru apni swarashi (Dhanu, Meena) ya uchch rashi (Karka) me ho. Balwan Guru Rahu ke prabhav ko kaafi had tak sambhaal leta hai.",
        "Guru aur Rahu ek rashi me hon par degree me kaafi door.",
        "Kisi aur shubh grah ki drishti us bhav par ho, ya bhav-swami balwan ho.",
        "Navamsha (D9) me Guru balwan ho.",
        "Lagnesh aur Chandra balwan hon; poora chart majboot ho to ek yog kahani tay nahi karta.",
      ],
    },
    {
      h2: "Kitna aam hai?",
      body: [
        "Guru ek rashi me lagbhag ek saal rehta hai aur Rahu lagbhag dedh saal. Kisi bhi samay janme vyakti ke chart me Guru aur Rahu ka ek rashi me hona lagbhag baarah me se ek chart me milta hai, aur Ketu ko bhi ginein to lagbhag chhah me se ek. Iska matlab yeh bhi hai ki kai mahinon tak us dauraan janme lagbhag sabhi logon ke chart me yeh yog hota hai. Itna vyapak yog akele kisi ki kismat nahi likhta.",
      ],
    },
    {
      h2: "Dasha ka role",
      body: [
        "Is yog ka asar aam taur par Guru ya Rahu ki mahadasha aur antardasha me zyada mehsoos kiya jaata hai. Rahu mahadasha me Guru ki antardasha, ya ulta, ko jyotishi khaas dhyaan se padhte hain. Apni current dasha free dekhkar samajh sakte hain ki yeh sawaal abhi aapke liye kitna relevant hai.",
      ],
      chips: [
        { to: "/mahadasha-calculator", label: "Mahadasha calculator" },
        { to: "/rahu-mahadasha", label: "Rahu mahadasha" },
        { to: "/guru-mahadasha", label: "Guru mahadasha" },
      ],
    },
    {
      h2: "Parampara ke shant upay",
      body: [
        "Parampara jo upay batati hai woh zyaadatar aacharan ke hain: guruon, adhyapakon aur buzurgon ka samman, Guruwar ko saadgi aur daan, adhyayan me anushaasan, aur jaldbaazi ke faislon se pehle kisi samajhdaar vyakti ki salah. Inme koi kharcha nahi aur nuksaan bhi nahi. Kisi mehenge 'dosh nivaran' package ki koi guarantee nahi hoti.",
      ],
      chips: otherDoshaChips,
    },
  ],
  faqs: [
    {
      q: "Guru Chandal dosh kaise banta hai?",
      a: "Jab kundli me Guru aur Rahu ek hi bhav me hon. Kuchh parampara Guru-Ketu yuti ya Guru par Rahu ki drishti ko bhi ginti hain.",
    },
    {
      q: "Kya Guru Chandal dosh hamesha bura hai?",
      a: "Nahi. Balwan Guru (Dhanu, Meena, Karka me) ya door degree ki yuti me asar kaafi kam maana jaata hai, aur kai logon me yahi yog aparamparik soch aur nayi field me safalta deta hai.",
    },
    {
      q: "Kya isse shiksha ya career ruk jaata hai?",
      a: "Aisa koi pakka niyam nahi. Yog lagbhag har baarahve chart me milta hai; career aur shiksha dasha, dashamesh, panchamesh aur poore chart par tikte hain.",
    },
    {
      q: "Guru Chandal dosh kab active hota hai?",
      a: "Aam taur par Guru ya Rahu ki mahadasha-antardasha me iske vishay zyada saamne aate hain. Mahadasha calculator se apni current dasha dekhiye.",
    },
    {
      q: "Iska saral upay kya hai?",
      a: "Parampara me guruon aur buzurgon ka samman, Guruwar ko daan aur saadgi, aur adhyayan me niyam. Yeh muft hain. Kisi upay ke parinaam ki guarantee nahi di ja sakti.",
    },
  ],
  schemaName: "Guru Chandal Dosh",
  schemaDescription:
    "Guru Chandal dosh (Jupiter conjunct Rahu) explained: the classical definition, cancellation conditions, how common it is, and why it is not a fixed verdict.",
  finalHeading: "Apna Guru, apni kundli me dekhiye",
  finalText:
    "Free kundli banaiye aur dekhiye ki aapke chart me Guru kahan hai, kitna balwan hai, aur Rahu se uska rishta kya hai.",
  articleSection: "Jyotish Glossary",
  disclaimer: doshaDisclaimer,
  relatedLinks: [
    { to: "/pitra-dosh", label: "pitra dosh" },
    { to: "/grahan-dosh", label: "grahan dosh" },
  ],
};

/* ------------------------------------------------------------ GRAHAN DOSH */

export const grahanDosh: LandingConfig = {
  url: "https://astroaura.ai/grahan-dosh",
  wide: true,
  title: "Grahan Dosh: Surya Chandra Ke Saath Rahu Ketu | Aura AI",
  description:
    "Grahan dosh tab maana jaata hai jab Surya ya Chandra Rahu-Ketu ke saath hon. Surya aur Chandra grahan dosh ka farak aur bhang ki sthitiyan yahan.",
  eyebrow: "Dosha Glossary",
  h1Pre: "Grahan Dosh:",
  h1Accent: "jab roshni par chhaaya ho",
  sub: "Surya ya Chandra ke saath Rahu-Ketu ka yog, janm ke samay grahan, aur woh sthitiyan jinme yeh dosh kamzor ya bhang maana jaata hai.",
  primaryCta: "Apni kundli free check kijiye",
  secondaryCta: "Jyotishi se poochhiye",
  lede: [
    "Aasmaan me grahan tab hota hai jab Surya aur Chandra Rahu ya Ketu ke bindu ke paas aate hain. Kundli me isi tasveer ko Grahan dosh kehte hain: jab janm-kundli me Surya ya Chandra kisi bhav me Rahu ya Ketu ke saath hon. Surya ke saath ho to Surya grahan dosh, Chandra ke saath ho to Chandra grahan dosh. Kuchh parampara janm ke din vaastavik grahan hone ko bhi isme ginti hain.",
  ],
  heroFacts: ["Surya ya Chandra + Rahu/Ketu", "Har ~6 me 1 chart (har ek ke liye)", "Bhang ke niyam hain"],
  sections: [
    {
      h2: "Grahan dosh ke do roop",
      subs: [
        {
          h3: "Surya grahan dosh",
          body: [
            "Surya aatma, pita, aatmavishwas aur adhikaar ka karak hai. Rahu ya Ketu ke saath hone par paramparik vyakhya me aatmavishwas me utaar-chadhaav, pita ya varishth logon se sambandh me ulajh, aur pehchaan ke sawaal joda jaata hai. Surya-Rahu yuti Pitra dosh ki paribhasha me bhi aati hai, isliye dono me overlap hai.",
          ],
        },
        {
          h3: "Chandra grahan dosh",
          body: [
            "Chandra mann, bhavnaon aur maata ka karak hai. Rahu ya Ketu ke saath hone par paramparik vyakhya me mann ki bechaini, zyada sochna ya bhavnatmak utaar-chadhaav joda jaata hai. Yeh jyotish ki bhasha hai, chikitsa nahi: agar mann lagataar bhaari rehta hai, to kisi mental health professional se baat karna sabse pehla aur sahi kadam hai.",
          ],
        },
      ],
      chips: doshaChips,
    },
    {
      h2: "Kab kamzor ya cancel maana jaata hai",
      bullets: [
        "Surya ya Chandra apni uchch rashi (Surya: Mesh, Chandra: Vrishabh) ya swarashi (Surya: Simha, Chandra: Karka) me ho.",
        "Guru ki drishti us bhav ya us grah par ho.",
        "Grah aur Rahu-Ketu ek rashi me hon par degree me door; asli 'grahan' jaisi sthiti nazdeek degree me hi maani jaati hai.",
        "Chandra Shukla paksha me balwan (zyada roshni wala) ho.",
        "Bhav-swami aur lagnesh balwan hon, aur Navamsha me grah ki sthiti achhi ho.",
      ],
    },
    {
      h2: "Kitna aam hai?",
      body: [
        "Chandra har mahine baarah rashiyon se guzarta hai, isliye lagbhag har mahine kuch din aise hote hain jab woh Rahu ki rashi me hota hai, aur kuch din Ketu ki. Rashi ke hisaab se lagbhag chhah me se ek chart me Chandra grahan dosh milta hai, aur Surya ke liye bhi lagbhag itna hi. Itna aam yog apne aap me jeevan ki disha tay nahi karta; bhav, rashi, degree aur drishti milkar asli tasveer banate hain.",
      ],
    },
    {
      h2: "Janm ke samay grahan: ek alag sawaal",
      body: [
        "Kuchh parivaar poochhte hain ki agar bachcha grahan ke din paida hua to kya hoga. Parampara me iske liye shanti path aur daan bataye gaye hain. Lekin aasmaan me grahan saal me kuch hi baar hota hai aur us din duniya bhar me laakhon bachche janm lete hain. Unke jeevan utne hi alag hote hain jitne kisi bhi aur din janme bachchon ke. Bachche ki kundli ko poore roop me padhiye, ek ghatna se nahi.",
      ],
      chips: [{ to: "/panchang", label: "Panchang" }],
    },
    {
      h2: "Parampara ke shant upay",
      body: [
        "Surya grahan dosh ke liye parampara subah Surya ko jal (arghya), pita aur varishthon ka samman, aur Raviwar ko saadgi batati hai. Chandra grahan dosh ke liye maata ki seva, Somwar ko saadgi, dhyan aur neend ka niyam. Yeh sab muft hain aur inka koi nuksaan nahi. Kisi mehenge upay ke parinaam ki guarantee nahi hoti.",
      ],
      chips: otherDoshaChips,
    },
  ],
  faqs: [
    {
      q: "Grahan dosh kya hota hai?",
      a: "Jab janm-kundli me Surya ya Chandra kisi bhav me Rahu ya Ketu ke saath hon. Surya ke saath Surya grahan dosh, Chandra ke saath Chandra grahan dosh.",
    },
    {
      q: "Kya grahan dosh kharab hi hota hai?",
      a: "Nahi. Uchch ya swarashi ka grah, Guru ki drishti, ya door degree ki yuti me asar kaafi kam maana jaata hai. Aur yeh yog lagbhag har chhathe chart me milta hai.",
    },
    {
      q: "Grahan ke din janme bachche ki kundli me kya dosh hota hai?",
      a: "Kuchh parampara ise grahan dosh maanti hai aur shanti path batati hai. Lekin bachche ka jeevan poori kundli se padha jaata hai, ek din ki ghatna se nahi.",
    },
    {
      q: "Chandra grahan dosh aur mann ki bechaini ka kya sambandh hai?",
      a: "Paramparik jyotish ise mann ke utaar-chadhaav se jodta hai. Lekin lagataar udaasi ya chinta medical sawaal hai; doctor ya counsellor se baat karna sabse pehla kadam hai.",
    },
    {
      q: "Grahan dosh aur Pitra dosh me farak?",
      a: "Surya-Rahu yuti dono me aati hai. Pitra dosh ka focus navam bhav aur poorvajon par hai; Grahan dosh ka focus Surya ya Chandra ki roshni par Rahu-Ketu ki chhaaya par.",
    },
  ],
  schemaName: "Grahan Dosh",
  schemaDescription:
    "Grahan dosh explained: Sun or Moon conjunct Rahu or Ketu in the birth chart, the Surya and Chandra forms, conditions that weaken or cancel it, and how common it is.",
  finalHeading: "Apne Surya aur Chandra ko janiye",
  finalText:
    "Free kundli banaiye aur dekhiye ki aapke Surya aur Chandra kahan hain, aur Rahu-Ketu unse kitni door.",
  articleSection: "Jyotish Glossary",
  disclaimer: doshaDisclaimer,
  relatedLinks: [
    { to: "/pitra-dosh", label: "pitra dosh" },
    { to: "/kemdrum-dosh", label: "kemdrum dosh" },
  ],
};

/* -------------------------------------------------------------- NADI DOSH */

export const nadiDosh: LandingConfig = {
  url: "https://astroaura.ai/nadi-dosh",
  wide: true,
  title: "Nadi Dosh: Gun Milan Me 0 Ank Aur Exceptions | Aura AI",
  description:
    "Nadi dosh tab hota hai jab var aur vadhu ki nadi ek ho, aur gun milan me 8 me se 0 ank milte hain. Teen nadi ka table, cancellation ke niyam aur FAQ.",
  eyebrow: "Dosha Glossary",
  h1Pre: "Nadi Dosh:",
  h1Accent: "8 ank ka sawaal",
  sub: "Ashtakoot milan ka sabse bhaari koot. Kaunsa nakshatra kis nadi me hai, Nadi dosh kab banta hai, aur kin sthitiyon me use cancel maana jaata hai.",
  primaryCta: "Kundli milan free kijiye",
  secondaryCta: "Jyotishi se poochhiye",
  lede: [
    "Nadi dosh kundli milan ka dosh hai, akeli kundli ka nahi. Ashtakoot gun milan ke 36 ank me se 8 ank Nadi koot ke hain, jo kisi bhi ek koot ke liye sabse zyada hai. Har nakshatra teen me se ek nadi me aata hai: Adi, Madhya ya Antya. Jab var aur vadhu ke janm nakshatra ek hi nadi ke hon, to Nadi koot ko 0 ank milte hain, aur ise Nadi dosh kehte hain. Poora gun milan aur baaki saat koot humare kundli matching page par samjhaye gaye hain; yeh page sirf Nadi par hai.",
  ],
  heroFacts: ["8 me se 0 ank", "Teen nadi: Adi, Madhya, Antya", "Classical exceptions hain"],
  sections: [
    {
      h2: "Kaunsa nakshatra kis nadi me",
      table: {
        head: ["Nadi", "Nakshatra"],
        rows: [
          ["Adi (Vata)", "Ashwini, Ardra, Punarvasu, Uttara Phalguni, Hasta, Jyeshtha, Mula, Shatabhisha, Purva Bhadrapada"],
          ["Madhya (Pitta)", "Bharani, Mrigashira, Pushya, Purva Phalguni, Chitra, Anuradha, Purva Ashadha, Dhanishtha, Uttara Bhadrapada"],
          ["Antya (Kapha)", "Krittika, Rohini, Ashlesha, Magha, Swati, Vishakha, Uttara Ashadha, Shravana, Revati"],
        ],
        caption:
          "Har nadi me 9 nakshatra. Apna janm nakshatra nakshatra calculator se dekhiye.",
      },
      body: [
        "Is table se ek seedha ganit nikalta hai: teen nadi me barabar nakshatra hain, isliye do anjaan logon ki nadi ek hone ki sambhavna lagbhag teen me se ek hai. Yaani Nadi dosh koi durlabh cheez nahi, lagbhag har teesre jode me aata hai. Isi wajah se iske exceptions jaanna zaroori hai.",
      ],
      chips: [
        { to: "/kundli-matching", label: "Kundli matching" },
        { to: "/nakshatra-calculator", label: "Nakshatra calculator" },
        ...doshaChips,
      ],
    },
    {
      h2: "Nadi dosh ko itna mahatva kyun",
      body: [
        "Paramparik vyakhya me Nadi ko sharirik prakriti (Vata, Pitta, Kapha) aur santaan se jodkar dekha gaya hai. Isliye kai parivaar high total score ke baavjood Nadi dosh par ruk jaate hain. Lekin yeh dhyaan rahe: yeh jyotish ki shastriya bhasha hai, genetic test nahi. Swasthya ya santaan se jude asli sawaalon ka jawaab doctor aur medical jaanch dete hain, nakshatra nahi.",
      ],
    },
    {
      h2: "Nadi dosh kab cancel maana jaata hai",
      body: [
        "Muhurat aur milan granthon me Nadi dosh ke kuch prasiddh exceptions bataye gaye hain. Inme se koi bhi lagu ho to dosh ko bhang maana jaata hai:",
      ],
      bullets: [
        "Dono ki rashi ek ho, lekin nakshatra alag.",
        "Dono ka nakshatra ek ho, lekin rashi alag (jo nakshatra do rashiyon me failte hain, unme yeh sambhav hai).",
        "Nakshatra ek ho, lekin charan (pada) alag.",
        "Kai parampara me: dono ki Chandra rashi ke swami ek hi grah hon, ya aapas me mitra hon.",
      ],
      subs: [
        {
          h3: "Score aur exception saath padhiye",
          body: [
            "Nadi ke 0 ank total ko kaafi neeche le aate hain, lekin exception lagu hone par jyotishi us 0 ko alag nazar se padhte hain. Isliye sirf number dekhkar rishta todna ya jodna, dono jaldbaazi hain. Humara kundli milan Nadi aur Bhakoot ke exceptions khud check karta hai aur saaf shabdon me batata hai.",
          ],
        },
      ],
    },
    {
      h2: "Agar Nadi dosh hai aur exception nahi",
      body: [
        "Parampara me Mahamrityunjaya jap, daan aur Vishnu ya Shiv ki aradhna jaise upay bataye gaye hain. Inka mool bhaav shraddha hai, aur yeh apne ghar me bina kharche ke bhi kiye jaate hain. Kai parivaar Nadi dosh ke saath bhi, poore chart ko dekhkar, shadi ka faisla lete hain aur sukhi rehte hain. Rishta do logon ki samajh, sammaan aur commitment par chalta hai; koi ek koot uska faisla nahi karta.",
      ],
      chips: [
        { to: "/manglik-calculator", label: "Manglik dosha" },
        { to: "/blog/manglik-dosha-explained", label: "Manglik dosha explained" },
        { to: "/vivah-muhurat-december-2026", label: "Vivah muhurat Dec 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "Nadi dosh kya hai?",
      a: "Jab var aur vadhu ke janm nakshatra ek hi nadi (Adi, Madhya ya Antya) ke hon. Tab gun milan me Nadi koot ke 8 me se 0 ank milte hain.",
    },
    {
      q: "Nadi dosh kab cancel hota hai?",
      a: "Jab dono ki rashi ek ho par nakshatra alag, ya nakshatra ek ho par rashi alag, ya nakshatra ek ho par charan alag. Kai parampara rashi-swami ek ya mitra hone par bhi ise bhang maanti hai.",
    },
    {
      q: "Kya Nadi dosh me shadi nahi karni chahiye?",
      a: "Yeh pakka niyam nahi. Pehle exceptions dekhiye, phir poora chart. Lagbhag har teesre jode me nadi ek hoti hai, aur kai aise jode sukhi jeevan jeete hain.",
    },
    {
      q: "Kya Nadi dosh se santaan me dikkat hoti hai?",
      a: "Paramparik jyotish Nadi ko santaan se jodta hai, lekin yeh medical sach nahi hai. Swasthya aur santaan ke sawaalon ke liye doctor ki salah lijiye.",
    },
    {
      q: "Nadi dosh aur Manglik dosh me farak?",
      a: "Nadi dosh dono kundliyon ke milan se banta hai. Manglik dosh ek kundli me Mangal ki sthiti se. Dono ke apne cancellation niyam hain; manglik ke liye humara manglik calculator dekhiye.",
    },
  ],
  schemaName: "Nadi Dosh",
  schemaDescription:
    "Nadi dosh in Ashtakoot kundli matching: the three nadis and their nakshatras, why it scores 0 of 8, how often it occurs, and the classical exceptions that cancel it.",
  finalHeading: "Score nahi, poora milan dekhiye",
  finalText:
    "Free kundli milan kijiye: aath koot, Nadi aur Bhakoot ke exceptions, aur Manglik check, sab ek saath, saaf bhasha me.",
  articleSection: "Jyotish Glossary",
  disclaimer: doshaDisclaimer,
  relatedLinks: [
    { to: "/kundli-matching", label: "kundli matching" },
    { to: "/manglik-calculator", label: "manglik calculator" },
  ],
};

/* ----------------------------------------------------------- KEMDRUM DOSH */

export const kemdrumDosh: LandingConfig = {
  url: "https://astroaura.ai/kemdrum-dosh",
  wide: true,
  title: "Kemdrum Dosh: Kemadruma Yog Aur Uska Bhang | Aura AI",
  description:
    "Kemdrum dosh tab banta hai jab Chandra se doosre aur baarahve bhav me koi grah na ho. Paribhasha, bhang ke niyam, aur yeh aksar cancel kyun hota hai.",
  eyebrow: "Dosha Glossary",
  h1Pre: "Kemdrum Dosh:",
  h1Accent: "akela Chandra",
  sub: "Chandra ke dono taraf khaali bhav ka yog, jise classical granth ek naam se jaante hain, aur jiske bhang ke niyam bhi utne hi saaf likhe hain.",
  primaryCta: "Apni kundli free check kijiye",
  secondaryCta: "Jyotishi se poochhiye",
  lede: [
    "Kemdrum dosh, jise classical granthon me Kemadruma yog kaha gaya hai, Chandra se juda yog hai. Jab janm-kundli me Chandra se doosre aur baarahve, dono bhavon me koi grah na ho, to Kemadruma banta hai. Ginti me Surya, Rahu aur Ketu nahi liye jaate. Pitra ya Guru Chandal ke ulat, yeh yog Brihat Parashara Hora Shastra, Saravali aur Phaladeepika jaise mool granthon me naam se milta hai, aur wahi granth iske bhang (cancellation) ke niyam bhi dete hain.",
  ],
  heroFacts: ["Chandra se 2 aur 12 khaali", "Surya, Rahu, Ketu nahi gine jaate", "Kai bhang niyam"],
  sections: [
    {
      h2: "Kemadruma yog ki classical paribhasha",
      bullets: [
        "Chandra se doosre bhav me koi grah na ho (Surya, Rahu, Ketu chhodkar).",
        "Chandra se baarahve bhav me bhi koi grah na ho.",
        "Kuchh granth Chandra ke saath kisi grah ki yuti na hona bhi shart maante hain.",
      ],
      body: [
        "Iska ulta bhi samjhiye: Chandra se doosre bhav me grah ho to Sunapha yog, baarahve me ho to Anapha yog, aur dono me ho to Durudhara yog banta hai. Yeh teenon shubh maane jaate hain. Kemadruma inka abhaav hai: mann ka karak Chandra 'akela' hai, isliye paramparik vyakhya me ise akelapan, asthirta ya sahare ki kami se joda gaya hai.",
      ],
      chips: [
        { to: "/moon-sign-calculator", label: "Moon sign calculator" },
        ...doshaChips,
      ],
    },
    {
      h2: "Kemadruma bhang: kab cancel hota hai",
      body: [
        "Yahi is yog ka sabse zaroori hissa hai, aur granth isme ekmat nahi hain; alag granth alag sthitiyan dete hain. Sabse zyada uddhrit bhang niyam yeh hain:",
      ],
      bullets: [
        "Chandra se kendra (1, 4, 7, 10) me koi grah ho.",
        "Lagna se kendra me koi grah ho, ya Chandra khud lagna se kendra me ho.",
        "Chandra par Guru ki drishti ho, ya Chandra kendra me ho aur shubh grah dekh rahe hon.",
        "Chandra balwan ho: Purnima ke paas, uchch (Vrishabh) ya swarashi (Karka) me.",
        "Chandra kisi grah ke saath yuti me ho.",
      ],
      subs: [
        {
          h3: "Isliye yeh aksar cancel ho jaata hai",
          body: [
            "Saat grahon me se kai ke kendra me hone ki sambhavna kaafi zyada hoti hai, isliye jin charts me Kemadruma banta bhi hai, unme se bahut se kisi na kisi bhang niyam me aa jaate hain. Kisi ko 'Kemdrum dosh hai' kehne se pehle yeh sab ginna zaroori hai. Humara AI jyotishi aapke chart par yahi ginti karta hai.",
          ],
        },
      ],
    },
    {
      h2: "Classical phal, aur use kaise padhein",
      body: [
        "Granth Kemadruma ke saath aarthik sangharsh, akelapan aur mann ki asthirta jaise phal likhte hain. Lekin wahi granth yeh bhi maante hain ki ek akela yog poore chart par bhari nahi padta. Lagnesh ka bal, dashamesh, Chandra ka paksha bal aur chal rahi dasha sab milkar tasveer banate hain. Chandra ki mahadasha ya antardasha me iske vishay zyada saamne aa sakte hain.",
        "Agar mann lagataar bhaari ya akela mehsoos hota hai, to yeh jyotish se pehle swasthya ka sawaal hai. Kisi apne se baat kijiye, aur zaroorat ho to mental health professional se. Yeh kamzori nahi, samajhdaari hai.",
      ],
      chips: [
        { to: "/chandra-mahadasha", label: "Chandra mahadasha" },
        { to: "/mahadasha-calculator", label: "Mahadasha calculator" },
      ],
    },
    {
      h2: "Parampara ke shant upay",
      body: [
        "Chandra ko balwan karne ke liye parampara saral cheezein batati hai: Somwar ko saadgi, maata aur maata-samaan logon ki seva, Shiv aradhna, dhyan, aur neend va dinacharya ka niyam. Rishton me jude rehna, jo Chandra ka asli vishay hai, apne aap me sabse bada upay hai. Inme koi kharcha nahi. Kisi mehenge upay ki koi guarantee nahi hoti.",
      ],
      chips: otherDoshaChips,
    },
  ],
  faqs: [
    {
      q: "Kemdrum dosh kya hai?",
      a: "Jab kundli me Chandra se doosre aur baarahve dono bhavon me koi grah na ho (Surya, Rahu, Ketu ko chhodkar). Classical naam Kemadruma yog hai.",
    },
    {
      q: "Kemdrum dosh kab cancel hota hai?",
      a: "Sabse uddhrit niyam: Chandra ya lagna se kendra me koi grah ho, Chandra par Guru ki drishti ho, Chandra balwan (Purnima ke paas, uchch ya swarashi) ho, ya Chandra kisi grah ke saath ho. Granthon me in niyamon par thoda matbhed hai.",
    },
    {
      q: "Kya Kemdrum dosh se garibi aati hai?",
      a: "Granth aarthik sangharsh ka phal likhte hain, lekin bhang ke niyam itne vyapak hain ki yeh yog aksar cancel ho jaata hai. Aur ek yog poora jeevan tay nahi karta; dhan ke liye doosra aur gyaarahva bhav, unke swami aur dasha dekhe jaate hain.",
    },
    {
      q: "Kemdrum aur Sunapha-Anapha yog me kya farak hai?",
      a: "Chandra se doosre bhav me grah ho to Sunapha, baarahve me ho to Anapha, dono me ho to Durudhara. Kemadruma tab hai jab dono khaali hon.",
    },
    {
      q: "Kya Kemdrum dosh ka upay zaroori hai?",
      a: "Pehle check kijiye ki bhang hai ya nahi. Parampara ke upay, jaise Somwar ki saadgi, maata ki seva aur dhyan, muft hain. Kisi upay ke parinaam ki guarantee nahi di ja sakti.",
    },
  ],
  schemaName: "Kemdrum Dosh",
  schemaDescription:
    "Kemadruma (kemdrum) yoga explained: no planets in the 2nd and 12th from the Moon, the classical cancellation rules, the related Sunapha and Anapha yogas, and balanced interpretation.",
  finalHeading: "Apna Chandra, apni kundli me",
  finalText:
    "Free kundli banaiye aur dekhiye ki aapke Chandra ke aas paas kaunse grah hain, aur bhang ke niyam lagu hote hain ya nahi.",
  articleSection: "Jyotish Glossary",
  disclaimer: doshaDisclaimer,
  relatedLinks: [
    { to: "/grahan-dosh", label: "grahan dosh" },
    { to: "/nadi-dosh", label: "nadi dosh" },
  ],
};

export const doshaPages: { path: string; config: LandingConfig }[] = [
  { path: "/pitra-dosh", config: pitraDosh },
  { path: "/guru-chandal-dosh", config: guruChandalDosh },
  { path: "/grahan-dosh", config: grahanDosh },
  { path: "/nadi-dosh", config: nadiDosh },
  { path: "/kemdrum-dosh", config: kemdrumDosh },
];
